import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';

/**
 * Everything this account owes a date on, in one place.
 *
 * `draft_due_date` and `publish_date` have existed on every campaign since the
 * marketplace shipped — buried in a terms table, on one campaign at a time.
 * A creator running five collaborations at once had no way to see next week,
 * which is the single most requested thing from anyone juggling more than two.
 *
 * Two views, one dataset. The month grid answers "how does the month look";
 * the agenda answers "what is next" and is the default, because that is the
 * question people actually arrive with — and because a grid is the wrong
 * shape for a phone.
 *
 * Hand-rolled rather than pulling in a calendar library: this project ships
 * no chart library either (see `DonutChart`), and a month grid is a `map` over
 * 42 cells.
 */

const URGENCY = {
    overdue: { dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200' },
    today: { dot: 'bg-amber-500', chip: 'bg-amber-50 text-amber-800 border-amber-200' },
    soon: { dot: 'bg-orange-400', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
    upcoming: { dot: 'bg-primary-500', chip: 'bg-primary-50 text-primary-700 border-primary-200' },
    done: { dot: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const TYPE_ICON = {
    draft_due: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    publish: 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
    review_deadline: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    application_deadline: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0',
    application_pending: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
};

const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Monday-first, because the product's users are on a Monday-first calendar. */
function monthMatrix(year, month) {
    const first = new Date(year, month, 1);
    const offset = (first.getDay() + 6) % 7;
    const start = new Date(year, month, 1 - offset);
    return Array.from({ length: 42 }, (_, i) =>
        new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

function EventRow({ event, t, compact = false }) {
    const tone = URGENCY[event.urgency] || URGENCY.upcoming;
    return (
        <div className={`flex items-center gap-2.5 rounded-xl border bg-white p-2.5 ${event.urgency === 'overdue' ? 'border-red-200' : 'border-slate-200'}`}>
            <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${tone.chip} border`}>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none"
                    viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round"
                        d={TYPE_ICON[event.type] || TYPE_ICON.draft_due} />
                </svg>
            </span>

            <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-slate-800 truncate">
                    {t(`calendar.type_${event.type}`)}
                    {event.planned && (
                        <span className="ml-1.5 text-[10px] font-semibold text-slate-400">
                            {t('calendar.planned')}
                        </span>
                    )}
                </p>
                <p className="text-[11px] text-slate-500 truncate">
                    {event.campaign_title}
                    {event.counterparty && <span className="text-slate-400"> · {event.counterparty}</span>}
                </p>
            </div>

            <div className="text-right flex-shrink-0">
                {!compact && (
                    <p className="text-[11px] font-semibold text-slate-600 tabular-nums">
                        {new Date(event.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                    </p>
                )}
                <span className={`inline-block px-1.5 py-0.5 rounded-md text-[9px] font-bold border ${tone.chip}`}>
                    {t(`calendar.urgency_${event.urgency}`)}
                </span>
            </div>
        </div>
    );
}

function CampaignCalendar() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('agenda');      // agenda | month
    const [cursor, setCursor] = useState(() => {
        const now = new Date();
        return { year: now.getFullYear(), month: now.getMonth() };
    });
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        let alive = true;
        api.get('/api/campaigns/calendar/')
            .then(res => { if (alive) setData(res.data); })
            .catch(() => { /* the interceptor surfaces auth/payment errors */ })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const byDate = useMemo(() => {
        const map = {};
        (data?.events || []).forEach(event => {
            (map[event.date] = map[event.date] || []).push(event);
        });
        return map;
    }, [data]);

    const agenda = useMemo(() => {
        // Overdue first — a missed date is the only thing on this screen that
        // needs acting on today. Everything else reads forward in time.
        const events = data?.events || [];
        const overdue = events.filter(e => e.urgency === 'overdue');
        const live = events.filter(e => e.urgency !== 'overdue' && e.urgency !== 'done');
        const done = events.filter(e => e.urgency === 'done');
        return { overdue, live, done };
    }, [data]);

    if (loading) {
        return <div className="flex justify-center py-16"><div className="spinner" /></div>;
    }
    if (!data) return null;

    const cells = monthMatrix(cursor.year, cursor.month);
    const monthLabel = new Date(cursor.year, cursor.month, 1)
        .toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    const shift = (delta) => setCursor(prev => {
        const next = new Date(prev.year, prev.month + delta, 1);
        return { year: next.getFullYear(), month: next.getMonth() };
    });

    const counts = data.counts || {};
    const hasAnything = (data.events || []).length > 0;

    return (
        <div className="space-y-3">
            {/* Counters double as the legend — the colours mean nothing until
                they are attached to a word somewhere. */}
            <div className="grid grid-cols-4 gap-2">
                {['overdue', 'today', 'soon', 'upcoming'].map(key => (
                    <div key={key}
                        className={`rounded-xl border p-2.5 text-center ${counts[key] ? URGENCY[key].chip : 'bg-white border-slate-200'}`}>
                        <p className="text-lg font-black tabular-nums leading-none">{counts[key] || 0}</p>
                        <p className="text-[10px] font-semibold mt-1 leading-tight opacity-80">
                            {t(`calendar.urgency_${key}`)}
                        </p>
                    </div>
                ))}
            </div>

            <div className="flex bg-white rounded-xl p-1 border border-slate-200">
                {['agenda', 'month'].map(key => (
                    <button key={key} onClick={() => setView(key)}
                        className={`flex-1 text-xs font-bold py-2 rounded-lg transition ${view === key
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'text-slate-500 hover:bg-slate-50'}`}>
                        {t(`calendar.view_${key}`)}
                    </button>
                ))}
            </div>

            {!hasAnything && (
                <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-300 mb-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-8 h-8" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                    <p className="text-sm font-bold text-slate-600">{t('calendar.empty')}</p>
                    <p className="text-xs text-slate-400 mt-1 max-w-xs leading-relaxed">
                        {t('calendar.emptyDesc')}
                    </p>
                </div>
            )}

            {/* ── Agenda ── */}
            {hasAnything && view === 'agenda' && (
                <div className="space-y-4">
                    {agenda.overdue.length > 0 && (
                        <div>
                            <p className="text-[11px] font-bold text-red-600 uppercase tracking-wide mb-1.5">
                                {t('calendar.overdueTitle')}
                            </p>
                            <div className="space-y-2">
                                {agenda.overdue.map((event, i) => (
                                    <EventRow key={`o${i}`} event={event} t={t} />
                                ))}
                            </div>
                        </div>
                    )}

                    {agenda.live.length > 0 && (
                        <div>
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                                {t('calendar.upcomingTitle')}
                            </p>
                            <div className="space-y-2">
                                {agenda.live.map((event, i) => (
                                    <EventRow key={`u${i}`} event={event} t={t} />
                                ))}
                            </div>
                        </div>
                    )}

                    {agenda.done.length > 0 && (
                        <details className="group">
                            <summary className="text-[11px] font-bold text-slate-400 uppercase tracking-wide cursor-pointer list-none select-none">
                                {t('calendar.doneTitle')} ({agenda.done.length})
                            </summary>
                            <div className="space-y-2 mt-1.5">
                                {agenda.done.map((event, i) => (
                                    <EventRow key={`d${i}`} event={event} t={t} />
                                ))}
                            </div>
                        </details>
                    )}
                </div>
            )}

            {/* ── Month grid ── */}
            {hasAnything && view === 'month' && (
                <div className="rounded-2xl bg-white border border-slate-200 p-3">
                    <div className="flex items-center justify-between mb-3">
                        <button onClick={() => shift(-1)}
                            className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center transition">‹</button>
                        <p className="text-sm font-bold text-slate-800">{monthLabel}</p>
                        <button onClick={() => shift(1)}
                            className="w-8 h-8 rounded-lg hover:bg-slate-100 text-slate-500 flex items-center justify-center transition">›</button>
                    </div>

                    <div className="grid grid-cols-7 gap-1 mb-1">
                        {[0, 1, 2, 3, 4, 5, 6].map(day => (
                            <span key={day} className="text-[10px] font-bold text-slate-400 text-center py-1">
                                {t(`calendar.weekday_${day}`)}
                            </span>
                        ))}
                    </div>

                    <div className="grid grid-cols-7 gap-1">
                        {cells.map(cell => {
                            const key = iso(cell);
                            const events = byDate[key] || [];
                            const otherMonth = cell.getMonth() !== cursor.month;
                            const isToday = key === data.today;
                            // The worst thing happening that day decides the
                            // dot: an overdue draft must not hide behind a
                            // completed publish on the same square.
                            const worst = ['overdue', 'today', 'soon', 'upcoming', 'done']
                                .find(level => events.some(e => e.urgency === level));

                            return (
                                <button key={key} onClick={() => setSelected(events.length ? key : null)}
                                    className={`aspect-square rounded-lg flex flex-col items-center justify-center gap-1 transition relative ${otherMonth ? 'opacity-30' : ''
                                        } ${selected === key ? 'bg-primary-100 ring-1 ring-primary-400'
                                            : isToday ? 'bg-primary-50 ring-1 ring-primary-300'
                                                : events.length ? 'bg-slate-50 hover:bg-slate-100' : 'hover:bg-slate-50'}`}>
                                    <span className={`text-[11px] tabular-nums ${isToday ? 'font-black text-primary-700' : 'text-slate-600'}`}>
                                        {cell.getDate()}
                                    </span>
                                    {worst && (
                                        <span className="flex gap-0.5">
                                            <span className={`w-1.5 h-1.5 rounded-full ${URGENCY[worst].dot}`} />
                                            {events.length > 1 && (
                                                <span className="text-[8px] font-bold text-slate-400 leading-none">
                                                    {events.length}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {selected && byDate[selected] && (
                        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">
                                {new Date(selected).toLocaleDateString('en-GB', {
                                    weekday: 'long', day: 'numeric', month: 'long',
                                })}
                            </p>
                            {byDate[selected].map((event, i) => (
                                <EventRow key={i} event={event} t={t} compact />
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default CampaignCalendar;
